/**
 * El plan de cuentas de Odoo como árbol, para el Balance de Comprobación y el
 * Libro Mayor.
 *
 * Aquí —y solo aquí— la jerarquía de `account.group` ES la estructura del
 * documento, porque el documento es el plan de cuentas. Es el equivalente a
 * «Hierarchy and Subtotals» del balance nativo de Odoo. En un estado financiero
 * la estructura la manda siempre el mapping del contador.
 *
 * Cada grupo vale la SUMA de sus descendientes, calculada al armar el árbol;
 * nunca una fila fija.
 */

import { useEffect, useState } from "react";
import { api } from "../api/client";
import { useAsync } from "../api/useAsync";
import type { SessionInfo, TreeNode, TreeSpec } from "../api/types";
import { DetailToolbar } from "./DetailToolbar";
import { EntriesDrawer } from "./EntriesDrawer";
import { ErrorState, Loading } from "./States";
import { TreeTable } from "./TreeTable";
import { expandToAccounts } from "./treeNodes";
import type { Period } from "./period";
import { useTreeState } from "./useTreeState";

interface Props {
  reportKey: string;
  spec: TreeSpec;
  period: Period;
  session: SessionInfo;
}

export function ChartTree({ reportKey, spec, period, session }: Props) {
  const estado = useTreeState(
    `${reportKey}:${period.periodFrom}:${period.periodTo}`,
    spec.default_mode,
  );
  const [nodoAbierto, setNodoAbierto] = useState<TreeNode | null>(null);

  // Ocultar un botón es comodidad, no autorización: el servidor exige
  // `journal:read` en el endpoint. Sin sesión con permisos —desarrollo local—
  // se ofrece todo, igual que hace `require()` en el backend.
  const puedeVerApuntes =
    !session.permissions || session.permissions.includes("journal:read");

  const { data: tree, error, loading } = useAsync(
    () =>
      api.reportTree(reportKey, {
        mode: estado.mode === "formal" ? "groups" : estado.mode,
        fullOdooChain: estado.fullChain,
        showZero: estado.showZero,
        periodFrom: period.periodFrom,
        periodTo: period.periodTo,
      }),
    [
      reportKey,
      estado.mode,
      estado.fullChain,
      estado.showZero,
      period.periodFrom,
      period.periodTo,
    ],
  );

  const nodos = tree?.nodes ?? [];

  // El Balance de Comprobación abre en jerarquía y con los grupos principales a
  // la vista. Desplegarlo entero pesaría lo mismo que la tabla plana —unos 360
  // nodos— y dejarlo del todo colapsado mostraría cinco filas. La siembra se
  // hace aquí y no en `useTreeState` porque necesita los nodos, que llegan del
  // servidor; y solo se aplica si esa pantalla no tiene nada guardado.
  const profundidadInicial = spec.initial_expanded_depth;
  const sembrar = estado.seed;
  useEffect(() => {
    if (!tree || profundidadInicial <= 0) return;
    sembrar(
      tree.nodes
        .filter((nodo) => nodo.has_children && nodo.depth < profundidadInicial)
        .map((nodo) => nodo.node_id),
    );
  }, [tree, profundidadInicial, sembrar]);

  return (
    <>
      <DetailToolbar
        mode={estado.mode}
        onMode={estado.setMode}
        fullChain={estado.fullChain}
        onFullChain={estado.setFullChain}
        showZero={estado.showZero}
        onShowZero={estado.setShowZero}
        onExpand={() => estado.expandAll(expandToAccounts(nodos))}
        onCollapse={estado.collapseAll}
        // Sin renglones formales encima no existe el «formato oficial»: el
        // documento empieza en los grupos del plan de cuentas.
        allowed={["groups", "accounts"]}
        offerFullChain={spec.full_chain}
      />

      {loading && <Loading what="el plan de cuentas" />}
      {error && <ErrorState error={error} />}

      {tree && !loading && (
        <>
          <TreeTable
            nodes={nodos}
            expanded={estado.expanded}
            onToggle={estado.toggle}
            onOpenEntries={puedeVerApuntes ? setNodoAbierto : undefined}
            /* Las columnas y el total los declara el SERVIDOR: el Balance de
               Comprobación lleva Saldo inicial · Debe · Haber · Saldo final y
               su fila de total, y en pantalla enseñaba solo dos de las cuatro
               y ninguna fila de total, aunque el Excel las traía todas. */
            moneyColumns={tree.money_columns}
            totalRow={tree.total_row}
          />
          <EntriesDrawer
            reportKey={reportKey}
            node={nodoAbierto}
            period={period}
            onClose={() => setNodoAbierto(null)}
          />
          <p className="small muted">
            Cada grupo es la suma de sus descendientes. Los grupos intermedios
            que solo contienen otro grupo no generan fila; su ruta completa en
            Odoo aparece en la columna de trazabilidad.
          </p>
        </>
      )}
    </>
  );
}
